/**
 * 업로드할 것을 고르는 두 가지 길 — 파일 선택창과 드래그앤드롭 — 을 같은 모양으로 맞춘다.
 *
 * 선택창은 File 에 `webkitRelativePath` 를 채워주지만 드롭은 그러지 않는다. 드롭된 폴더를
 * 여는 방법은 File and Directory Entries API(`webkitGetAsEntry`) 하나뿐이고, 그쪽은 폴더를
 * 직접 걸어야 한다. 그래서 경로를 File 에 기대지 않고 여기서 따로 들고 다닌다.
 */

/** 파일 하나와, 놓인 폴더를 기준으로 어디에 들어갈지. */
export interface PickedFile {
  file: File
  /** `photo.jpg`, 폴더째 골랐다면 `2024/여름/photo.jpg`. */
  path: string
}

export interface PickedContents {
  files: PickedFile[]
  /** 안에 파일이 하나도 없는 폴더. 업로드가 만들어주지 않으므로 따로 만들어야 남는다. */
  emptyFolders: string[]
}

export function pickedFromInput(list: FileList): PickedContents {
  return {
    files: [...list].map((file) => ({
      // webkitdirectory 로 고르면 폴더 구조가, 그냥 고르면 빈 문자열이 온다.
      file,
      path: file.webkitRelativePath || file.name,
    })),
    // 선택창은 파일만 알려준다. 빈 폴더는 여기 닿기 전에 이미 사라진 뒤다.
    emptyFolders: [],
  }
}

/** 이 드래그가 우리가 받을 것인지. 행을 끌거나 글을 끄는 것과 갈라야 한다. */
export function carriesFiles(transfer: DataTransfer): boolean {
  return transfer.types.includes('Files')
}

export async function pickedFromDrop(transfer: DataTransfer): Promise<PickedContents> {
  // items 는 drop 핸들러가 반환되는 순간 비워진다. 첫 await 전에 전부 꺼내둬야 한다.
  const entries = [...transfer.items]
    .map((item) => item.webkitGetAsEntry())
    .filter((entry): entry is FileSystemEntry => entry !== null)

  // 이 API 가 없는 브라우저. 원래도 폴더는 못 받았을 자리라, 평평한 목록으로 물러선다.
  if (entries.length === 0) {
    return {
      files: [...transfer.files].map((file) => ({ file, path: file.name })),
      emptyFolders: [],
    }
  }

  const contents: PickedContents = { files: [], emptyFolders: [] }
  for (const entry of entries) await collect(entry, '', contents)
  return contents
}

async function collect(
  entry: FileSystemEntry,
  prefix: string,
  into: PickedContents,
): Promise<void> {
  const path = prefix === '' ? entry.name : `${prefix}/${entry.name}`

  if (isFile(entry)) {
    into.files.push({ file: await fileOf(entry), path })
    return
  }
  if (!isDirectory(entry)) return

  const children = await readAll(entry)
  if (children.length === 0) {
    into.emptyFolders.push(path)
    return
  }
  for (const child of children) await collect(child, path, into)
}

/**
 * `readEntries` 는 한 번에 전부 주지 않는다 — 크롬은 100개씩 끊어 주고, 빈 배열로 끝을
 * 알린다. 한 번만 부르면 사진 폴더의 앞 100장만 올라간다.
 */
function readAll(directory: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
  const reader = directory.createReader()
  const all: FileSystemEntry[] = []

  return new Promise((resolve, reject) => {
    const readNext = () =>
      reader.readEntries((batch) => {
        if (batch.length === 0) {
          resolve(all)
          return
        }
        all.push(...batch)
        readNext()
      }, reject)
    readNext()
  })
}

function fileOf(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}

// 이 API 는 어느 쪽이든 기반 타입으로 건네주고 종류는 불리언으로 알려준다. 좁히는 길이
// 이것뿐이다.
function isFile(entry: FileSystemEntry): entry is FileSystemFileEntry {
  return entry.isFile
}

function isDirectory(entry: FileSystemEntry): entry is FileSystemDirectoryEntry {
  return entry.isDirectory
}
